import { useNavigate } from 'react-router';

import { LogoutReq_LogoutOption } from '@/shared/api/protocol-ts/api/auth/svc_authentication_pb';
import { AnalyticsEventName, IS_HAPPYDESK_CHAT, PATHS } from '@/shared/config';
import { handleHappydeskLogout, useAppDispatch } from '@/shared/hooks';
import {
  clearAgentChatPreferences,
  clearDemoSession,
  clearPresignMFAExempt,
  resetFaroUser,
  trackEvent,
} from '@/shared/lib';
import { resetState } from '@/shared/model';

import { authModel } from '@/entities/auth';
import { organizationModel } from '@/entities/organization';

import { clearPresignUrl } from '@/features/presignAuth';

import { ForcedLogoutReason, setForcedLogoutNotice } from '../lib';

type UseLogoutOptions = {
  // A logout the user did not ask for must not land in the `Logout Success`
  // funnel, so every system-initiated caller reports under its own name.
  eventName?: AnalyticsEventName;
  notice?: ForcedLogoutReason;
  redirectTo?: string;
};

export const useLogout = (options?: UseLogoutOptions) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  return async () => {
    trackEvent({
      eventName: options?.eventName ?? 'Logout Success',
    });

    await dispatch(
      authModel.thunks.logout({ Option: LogoutReq_LogoutOption.None }),
    ).unwrap();

    // TODO: REMOVE dispatch and localstorage cleaner when auth will work on new gRPC lib.
    // Think about to encapsulate this into thunk
    localStorage.removeItem('user');
    clearPresignUrl();
    clearPresignMFAExempt();
    clearDemoSession();
    clearAgentChatPreferences();
    window.dispatchEvent(new Event('storage'));
    dispatch(organizationModel.actions.removeAll());
    dispatch(resetState());
    resetFaroUser();

    if (IS_HAPPYDESK_CHAT) {
      handleHappydeskLogout();
    } else {
      window.HubSpotConversations?.clear({ resetWidget: true });
      window.hsConversationsSettings = {
        loadImmediately: false,
      };
      window.HubSpotConversations?.widget.load();
    }

    if (options?.redirectTo) {
      if (options.notice) {
        setForcedLogoutNotice(options.notice);
      }

      // NOTE: a hard reload, not navigate() — the user/organization streams are
      // opened once on mount, so only a full page load restarts them under the
      // new anonymous session.
      window.location.replace(options.redirectTo);

      return;
    }

    navigate(PATHS.signIn);
  };
};
